import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Bar, BarChart, CartesianGrid, Label, Tooltip, XAxis, YAxis, ResponsiveContainer
} from 'recharts';
import { Button } from 'react-bootstrap';

const DeveloperBarChart = ({ data }) => {
    const [activeBars, setActiveBars] = useState({
        totalCommits: true,
        totalPRs: true,
    });

    const toggleBar = (dataKey) => {
        setActiveBars((prev) => ({
            ...prev,
            [dataKey]: !prev[dataKey],
        }));
    };

    const chartData = data.map(element => ({
        developer: element.developer ? element.developer.replace(" ", "\n") : "Unknown",
        totalPRs: element.totalMergedPrs + element.totalOpenPrs + element.totalDeclinedPrs,
        totalCommits: element.totalCommits
    }));

    const customLegend = () => (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', padding: '10px' }}>
            <Button
                variant='link'
                onClick={() => toggleBar('totalCommits')}
                style={{
                    cursor: 'pointer',
                    color: activeBars.totalCommits ? '#8884d8' : '#ccc',
                    fontWeight: activeBars.totalCommits ? 'bold' : 'normal',
                    textDecoration: 'none'
                }}
            >
                Total Commits
            </Button>
            <Button
                variant='link'
                onClick={() => toggleBar('totalPRs')}
                style={{
                    cursor: 'pointer',
                    color: activeBars.totalPRs ? '#82ca9d' : '#ccc',
                    fontWeight: activeBars.totalPRs ? 'bold' : 'normal',
                    textDecoration: 'none'
                }}
            >
                Total PRs
            </Button>
        </div>
    );
    const renderCustomBarLabel = ({ x, y, width, value }) => {
        if (!value) return null;
        return (
            <text
                x={x + width / 2}
                y={y + 15}
                textAnchor="middle"
                fill="#fff"
                fontSize={12}
                fontWeight="bold"
            >
                {value}
            </text>
        );
    };
    const barSize = 40;
    const totalBarSpace = chartData.length * barSize;
    const chartMinWidth = totalBarSpace + chartData.length * 20;
    return chartData.length > 0 ? (
        <div style={{
            border: '1px solid #ccc',
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9',
            textAlign: 'center',
            overflowX: 'auto',
            height: '100%',
            width: '100%'
        }}>
            <h4>Commits & PRs Count By Developers</h4>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                {customLegend()}
            </div>
            <div style={{ minWidth: `${chartMinWidth}px`, height: '500px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
                        barCategoryGap={chartData.length < 6 ? 80 : 20}
                        barGap={4}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="developer" interval={0} height={40} tick={customTick}>
                            <Label
                                value="Developers"
                                position="insideBottom"
                                offset={-22}
                                style={{ fill: "black" }}
                            />
                        </XAxis>
                        <YAxis>
                            <Label
                                angle={270}
                                position="insideLeft"
                                value="Count"
                                style={{
                                    fontSize: "100%",
                                    fill: "black",
                                    textAnchor: "insideTop",
                                }}
                            />
                        </YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        {activeBars.totalCommits && (
                            <Bar
                                dataKey="totalCommits"
                                fill="#8884d8"
                                label={renderCustomBarLabel}
                                barSize={barSize}
                            />
                        )}
                        {activeBars.totalPRs && (
                            <Bar
                                dataKey="totalPRs"
                                fill="#82ca9d"
                                label={renderCustomBarLabel}
                                barSize={barSize}
                            />
                        )}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    ) : null;
};
// Tooltip component
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
        return (
            <div style={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                padding: '10px',
                borderRadius: '5px',
            }}>
                <p><strong>{label.replace('\n', ' ')}</strong></p>
                {payload.map((entry) => {
                    let name = entry.name;
                    if (name === 'totalCommits') name = 'Total Commit';
                    if (name === 'totalPRs') name = 'Total PR';
                    return (
                        <p key={entry.name} style={{ color: entry.color, margin: 0 }}>
                            {name}: {entry.value}
                        </p>
                    );
                })}
            </div>
        );
    }
    return null;
};
// Tick formatter for multi-line labels
const customTick = ({ x, y, payload }) => {
    const lines = payload.value.split(/[\s\-\\]/);
    const angle = -35
    return (
        <g transform={`translate(${x},${y + 20}) rotate(${angle})`}>
            {lines.map((line, index) => (
                <text
                    key={`${line}_${index}`}
                    x={0}
                    y={index * 12}
                    textAnchor="middle"
                    fill="#666"
                    fontSize={12}
                >
                    {line}
                </text>
            ))}
        </g>
    );
};
// PropTypes
DeveloperBarChart.propTypes = {
    data: PropTypes.array.isRequired,
};
CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
        color: PropTypes.string
    })),
    label: PropTypes.string,
};
export default DeveloperBarChart;